/**
 * ⭐⭐⭐ `R2-UI8` · **La pestaña «Botones de respuestas automáticas»: la LISTA de
 * un brazo, su panel y su popup.** `// ADR-0348`
 * `R2-F3-CA-01`…`CA-12`, `CA-14`…`CA-39`, `CA-72`. `PRD_R2.md:1081-1148`, `:1346`.
 *
 * ════════════════════════════════════════════════════════════════════════════
 * ⛔⛔⛔ ① DÓNDE VIVE ESTA PANTALLA, Y POR QUÉ NO EN
 *      `src/respuestas-automaticas/`
 * ════════════════════════════════════════════════════════════════════════════
 *
 * `PRD_R2.md:1083`, textual: *«La funcionalidad vive como una pestaña adentro de
 * Plantillas, que es donde están los botones: **no hay ítem en el menú
 * lateral**»* (`CA-04`).
 *
 * ⇒ reescribir la maqueta en su lugar deja dos verdades a la vez: un directorio
 * cuyo nombre **es el de un ítem de menú que el PRD prohíbe**, montado desde
 * otro. Y dejarla al lado «por si acaso» es la opción (c) de `F3.md` Q15: dos
 * pantallas para lo mismo, y nadie puede decir cuál está mirando el operador.
 *
 * ⇒ **`src/respuestas-automaticas/` SE BORRA** (`PRD_R2.md:2494`, `B24`), junto
 * con `pruebas/aw8-respuestas-automaticas.test.tsx`. Lo que se pone rojo por
 * diseño y quién lo cierra está en `ADR-0348`.
 *
 * ════════════════════════════════════════════════════════════════════════════
 * ⛔⛔ ② «DÓNDE APARECE» LLEGA CALCULADO. ESTA PANTALLA NO CRUZA NADA
 * ════════════════════════════════════════════════════════════════════════════
 *
 * `A10-2`, `ADR-0247`, `contracts/admin-api/index.ts` ⟦donde-aparece-se-calcula⟧.
 *
 * La tentación está a un paso: **la otra pestaña ya tiene el catálogo de F2
 * abierto**, así que cruzarlo acá «ahorra un campo en el cable». Rompe de dos
 * formas que no se ven:
 *
 *   · **normaliza distinto que el módulo** (`F3.md` Q4): una fila diría «no está
 *     en ninguna plantilla» mientras el runtime la encuentra y contesta. La
 *     comparación la hace `normalizeButtonText` de `contracts/channel`, que es
 *     **la misma función con la que el runtime busca al apretar el botón**;
 *   · **son DOS lecturas** —el catálogo entero ∩ la lista de envíos del brazo—
 *     que sólo el servidor tiene en la misma transacción. Desde el navegador son
 *     dos pedidos con dos instantes, y una sincronización en el medio deja la
 *     columna afirmando algo que ya no es cierto.
 *
 * ⇒ acá `apareceEn` sólo se **redacta**: `join(" · ")`, o la frase de vacío. El
 * copy no viaja por el cable y el veredicto no se recalcula en el navegador.
 *
 * ════════════════════════════════════════════════════════════════════════════
 * ⛔ ③ EL PAGINADO ES DEL SERVIDOR, NO UN `slice()`
 * ════════════════════════════════════════════════════════════════════════════
 *
 * Regla 19 (`PRD_R2.md:1251`) y `CA-72`: orden de creación, **50 por página**. El
 * cable tiene `?pagina=&tamano=` y devuelve `total` **del brazo entero**.
 *
 * ⛔ **Por eso NO se hace `filas.slice(...)` sobre una lectura completa**, que es
 * lo que la pestaña de al lado sí hace —y su propio comentario declara por qué es
 * correcto ALLÁ: su borde no pagina—. Acá el borde pagina, así que traer todo y
 * cortar en el navegador sería traer la tabla entera del brazo en cada pintado y
 * dejar `total` mintiendo el día que alguien lo mire.
 *
 * ⛔ **Y el orden es el que devolvió el servidor: no se ordena acá.** `createdAt`
 * viaja para que la fila sea reconciliable, **no** para que la pantalla reordene:
 * re-ordenar en el navegador sobre una PÁGINA ordena 50 filas dentro de un corte
 * que el servidor ya hizo con otro criterio, y el resultado se ve plausible y es
 * otro.
 *
 * ════════════════════════════════════════════════════════════════════════════
 * ⭐⭐ ④ LAS DOS APERTURAS DEL PANEL, Y LA ASIMETRÍA ES EL CRITERIO
 * ════════════════════════════════════════════════════════════════════════════
 *
 * `CA-14`/`CA-15`: «Nueva respuesta automática» tiene **dirección propia** y
 * sobrevive a la recarga ⇒ la enciende la `<Route>`, por la prop `panelNuevo`.
 * `CA-29`: «Editar» abre el mismo panel y la dirección **sigue en `…/botones`**
 * ⇒ la enciende un `useState`.
 *
 * ⛔⛔ **Darle dirección propia a «Editar» es lo simétrico y `CA-29` lo
 * PROHÍBE.** El control negativo que lo separa: **recargar estando en «Editar»
 * NO vuelve a abrir el panel**. Sin ese caso, las dos implementaciones se ven
 * idénticas.
 *
 * ════════════════════════════════════════════════════════════════════════════
 * ⚠️ ⑤ DE DÓNDE SALE EL BRAZO, Y LA CAPACIDAD
 * ════════════════════════════════════════════════════════════════════════════
 *
 * El brazo lo da la RUTA (`ADR-0136` ②) y sin brazo la pantalla **falla cerrada y
 * dice por qué**: no inventa un default, porque la lista del brazo equivocado
 * servida en silencio es el daño que esto existe para impedir.
 *
 * La capacidad de derivación sale de `derivacionHabilitadaDe(config, brazo)`
 * (`ADR-0362`), que devuelve **`boolean | null`**, y `null` **no** es `false`.
 * Ver `./panel.tsx` §④, que es donde eso cambia lo que se dibuja.
 */

import { useCallback, useEffect, useMemo, useState } from "react"
import type { JSX } from "react"
import { useNavigate } from "react-router"
import type { AutoReplyAction, AutoReplyId } from "@contracts/admin-api/index.ts"
import { useBrazo } from "../../brazo.ts"
import { useConfiguracion } from "../../contexto-configuracion.tsx"
import { derivacionHabilitadaDe } from "../../configuracion.ts"
import { Paginado } from "../../paginado.tsx"
import { rutaDeLaPantalla, SEGMENTOS } from "../../rutas.ts"
import { esBrazo, selloDelBrazo } from "../../zonas.ts"
import type { Brazo } from "../../zonas.ts"
import { crearBordeBotonesHttp, esRepetida } from "./borde-botones.ts"
import type { BordeBotones, DatosDelPanel, FilaDeRespuesta } from "./borde-botones.ts"
import { mensajeDeRepetida, PanelDeRespuesta } from "./panel.tsx"
import { PopupEliminar } from "./popup-eliminar.tsx"

// ⟦rastro · ADR-0082 · R4-UIT1 · 4/10/2026⟧ El texto viejo de esta columna («Qué responde KAI»,
// PRD_R4.md:1980) y el de la nota de la pestaña («… qué responde KAI …», PRD_R4.md:1981) quedan
// conservados acá en comentario; el texto visible pasó a «el chatbot».

/**
 * Las CINCO columnas de `PRD_R2.md:1090`, en su orden (`CA-06`).
 *
 * ⛔ Van en una LISTA y no en un `if` del `<tbody>`: el encabezado y las celdas
 * salen de la misma fuente, así que no pueden quedar corridos.
 */
export const COLUMNAS = [
  "Botón",
  "Dónde aparece",
  "Qué responde el chatbot",
  "Además de responder",
  "Acciones",
] as const

/**
 * ⭐ **Los rótulos de la COLUMNA, que NO son los del panel** (`PRD_R2.md:1093`,
 * `CA-10`).
 *
 * ⚠️ La tabla dice *«Marca «Siguió con el chatbot»»* y el panel dice *«Marcar que
 * siguió con el chatbot»*. **No es un descuido y no se unifican**: uno describe
 * lo que la fila HACE (indicativo, se lee de un vistazo en cincuenta renglones) y
 * el otro es la acción que el operador ELIGE (infinitivo). Los dos están en el
 * PRD con esas palabras y los dos tienen CA propios.
 *
 * ⛔ El tipo va ANOTADO por el mismo motivo que `OPCIONES` en `./panel.tsx`: una
 * acción nueva en el contrato deja esto sin compilar en vez de dibujar una celda
 * vacía.
 */
export const ROTULO_EN_LA_TABLA: Readonly<Record<AutoReplyAction, string>> = {
  ninguna: "Sólo responder",
  siguio_con_chatbot: "Marca «Siguió con el chatbot»",
  pidio_humano: "Marca «Pidió humano»",
  opt_out: "Da de baja (opt-out)",
}

/** `CA-09` / `PRD_R2.md:1092` · lo que dice la columna cuando `apareceEn` viene vacío. */
export const SIN_PLANTILLAS = "El botón no está en ninguna plantilla de este brazo."

/**
 * `CA-05` · la nota de la pestaña.
 *
 * ⚠️⚠️ **DIVERGENCIA DE TEXTO, DECLARADA Y NO RESUELTA ACÁ.** `CA-05`
 * (`PRD_R2.md:1279`) dice *«Los botones se crean…»* y `PRD_R2.md:1089` dice *«Los
 * botones **de respuesta rápida** se crean…»*. Se escribe **el de `:1089`**, que
 * CONTIENE al del CA —así el `toContain` del criterio pasa con el texto más
 * preciso— y queda anotado para la PO. Ver `ADR-0348`.
 *
 * ⚠️⚠️ **Y la palabra «Acá» de este texto está en la lista `VOSEO` de
 * `pruebas/r11-textos.test.tsx`.** Es del PRD, palabra por palabra, y el CA es
 * textual: se escribe como el PRD lo pide y **el conflicto va reportado**, no
 * tapado cambiándole la palabra al criterio.
 *
 * ⛔⭐ **RASTRO · 24/9/2026, `R2-UI14`** (errata `R2-E2` de
 * `docs/prd/PRD_R2-errata.md`). El párrafo de arriba **dejó de ser cierto**: el
 * conflicto que reportaba lo resolvió su dueño. `R2-E2` decide que en los textos
 * prescritos rige «aquí» y no «acá» —la consola declara `es-CL`—, nombra
 * `PRD_R2.md:1279` (`CA-05`, «alinealo también con aquí») y, desde la noche del
 * mismo día, también `:1089`, que es el texto que esta constante dibuja. ⇒ la
 * nota dice **«Aquí se configura…»**, y el barrido `VOSEO` de
 * `pruebas/r11-textos.test.tsx` vuelve a verde **sin excepción nueva**: se cambió
 * la palabra de la pantalla, no la lista. Se cambió SÓLO esa palabra.
 *
 * ⛔ **Y el primer párrafo tenía una afirmación falsa**, que se deja escrita para
 * que se vea: el texto de `:1089` **no CONTIENE** al del CA —«Los botones se
 * crean…» no es subcadena de «Los botones de respuesta rápida se crean…»—, así
 * que un `toContain` de la nota entera de `:1279` nunca pasó. Lo resuelve la
 * errata `R2-E5` (Galo, 24/9/2026 noche): **rige `:1089`**, «de respuesta
 * rápida» incluido, y `CA-05` se lee con ese texto. La divergencia ya no está
 * «no resuelta»: está decidida a favor de lo que ya se escribía.
 */
export const NOTA_DE_LA_PESTANA =
  "Los botones de respuesta rápida se crean en el panel de Meta, adentro de la plantilla. Aquí se configura qué responde el chatbot cuando un participante los aprieta."

/**
 * ⭐ `CA-72` / regla 19 · **50 filas por página, y NO `config.pageSize`** (que
 * vale 10).
 *
 * El número es del criterio y no de la instancia: una instancia que bajara
 * `pageSize` a 10 partiría en dos la lista de doce filas de `F3.md` §3.3 y dejaría
 * `CA-72` («Página 1 de 1») rojo sin que nadie hubiera tocado esta pantalla. Es
 * el mismo argumento que `plantillas/pantalla.tsx` ya dejó escrito para su 50.
 */
export const FILAS_POR_PAGINA = 50

/**
 * Cuántos puntos de código de la respuesta se muestran en la celda antes del «…».
 *
 * `PRD_R2.md:1094`: *«El texto de la respuesta, recortado con «…» si no entra»*.
 * El PRD **no da el número**, así que éste es nuestro y va dicho: el recorte es
 * de la CELDA, nunca del dato —`replyText` viaja entero (`RespuestaAutomatica`:
 * *«Entero: el recorte con «…» es de la pantalla»*) y el panel de editar muestra
 * el texto completo—.
 *
 * ⚠️ Se cuenta con el spread y no con `.length`, por lo mismo que el contador:
 * cortar por unidades UTF-16 puede partir un par de subrogados al medio y dejar
 * media letra en la celda.
 */
export const RECORTE_DE_LA_RESPUESTA = 120

export function recortar(texto: string): string {
  const puntos = [...texto]
  return puntos.length <= RECORTE_DE_LA_RESPUESTA
    ? texto
    : `${puntos.slice(0, RECORTE_DE_LA_RESPUESTA).join("")}…`
}

/**
 * ⭐⭐ El estado del PEDIDO, en TRES variantes.
 *
 * ⛔ **«No se pudo preguntar» NO es «la lista está vacía»** (`ADR-0244`, guard ⑥
 * de `AW46`): una respuesta que llegó VACÍA y una que NO LLEGÓ confundidas dejan
 * una lista vacía que además parece un hecho, *«que es la peor de las dos
 * mitades»*. El estado vacío de `PRD_R2.md:1096` sólo se dibuja con
 * `caso === "traido"`.
 */
type EstadoDeLaLista =
  | { readonly caso: "pidiendo" }
  | { readonly caso: "traido"; readonly respuestas: readonly FilaDeRespuesta[]; readonly total: number }
  | { readonly caso: "no-se-pudo-preguntar"; readonly mensaje: string }

const PIDIENDO: EstadoDeLaLista = { caso: "pidiendo" }

/** Lo que una escritura dejó, para poder decirlo sin inventarlo. */
type EnCurso = { readonly enCurso: boolean; readonly error: string | null }

const QUIETO: EnCurso = { enCurso: false, error: null }

function mensajeDe(causa: unknown): string {
  return causa instanceof Error ? causa.message : String(causa)
}

export function PantallaBotones({
  bordeBotones,
  panelNuevo = false,
}: {
  /**
   * ⭐ El borde, **inyectable**. Por defecto se arma contra la `admin-web` de
   * `configuracion.json`.
   *
   * ⚠️ Va con default en vez de por parámetro de `App.tsx` §`pantallas()` **a
   * propósito**: esta etapa no toca el marco, y el borde se arma de `config`, que
   * ya está disponible por `useConfiguracion()` —un contexto que YA existe, no
   * uno nuevo—. Agregar un contexto sí cambiaría lo que monta
   * `gates/pantalla-envios.spec.ts`, que los descubre barriendo `src/contexto-*.tsx`.
   */
  readonly bordeBotones?: BordeBotones
  /**
   * ⭐⭐ **La costura del panel «Nueva respuesta automática»: la enciende LA
   * DIRECCIÓN.** `CA-14`/`CA-15`. Ver el bloque ④.
   *
   * ⛔ Por eso es una prop y no un `useState`: `CA-15` pide que al RECARGAR
   * `…/botones/nueva` el panel siga abierto. Un estado local se pierde en la
   * recarga y los dos criterios quedarían en rojo **con cara de verde**.
   */
  readonly panelNuevo?: boolean
}): JSX.Element {
  const irA = useNavigate()
  const config = useConfiguracion()
  const brazoDeLaRuta = useBrazo()
  /** ⛔ `null` es «acá no se declaró ningún brazo», no un default. Falla cerrada. */
  const brazo: Brazo | null = esBrazo(brazoDeLaRuta) ? brazoDeLaRuta : null

  const borde = useMemo(
    () => bordeBotones ?? crearBordeBotonesHttp(config),
    [bordeBotones, config],
  )

  const [estado, setEstado] = useState<EstadoDeLaLista>(PIDIENDO)
  const [recarga, setRecarga] = useState(0)
  const [pagina, setPagina] = useState(1)

  /** ⭐ Ver el bloque ④: «Editar» es ESTADO, y por eso la recarga lo cierra. */
  const [editando, setEditando] = useState<FilaDeRespuesta | null>(null)
  const [eliminando, setEliminando] = useState<FilaDeRespuesta | null>(null)
  const [guardando, setGuardando] = useState<EnCurso>(QUIETO)
  const [borrando, setBorrando] = useState<EnCurso>(QUIETO)

  const recargar = useCallback(() => setRecarga((n) => n + 1), [])

  /**
   * La lectura de la página. Un solo brazo, el de la ruta.
   *
   * ⛔ **Sin brazo no se pide nada**: el brazo va en la ruta del borde, así que un
   * pedido sin brazo no existe en el cable.
   */
  useEffect(() => {
    if (brazo === null) return

    let cancelado = false
    setEstado(PIDIENDO)

    borde
      .listar(brazo, pagina, FILAS_POR_PAGINA)
      .then((leida) => {
        if (!cancelado) {
          setEstado({ caso: "traido", respuestas: leida.respuestas, total: leida.total })
        }
      })
      .catch((causa: unknown) => {
        if (!cancelado) setEstado({ caso: "no-se-pudo-preguntar", mensaje: mensajeDe(causa) })
      })

    return () => {
      cancelado = true
    }
  }, [borde, brazo, pagina, recarga])

  const sello = brazo === null ? null : selloDelBrazo(brazo)

  /**
   * ⛔⛔ **La capacidad entra por CONFIG y `null` NO es `false`** (`ADR-0362`).
   * Ver `./panel.tsx` §④. Acá sólo se lee y se pasa: colapsarlo con un `?? false`
   * compila y borra para siempre la diferencia entre «declaró que no» y «no
   * declaró nada».
   */
  const derivacionHabilitada = brazo === null ? null : derivacionHabilitadaDe(config, brazo)

  /**
   * ⭐ La dirección a la que vuelven «Cancelar», Escape y el éxito del alta.
   *
   * ⚠️ **La de IDA (`…/botones/nueva`) no vive acá**: la dibuja el botón «Nueva
   * respuesta automática» del encabezado, que es de `../pantalla.tsx` porque
   * `CA-02` lo pone en el encabezado de la pestaña. Esta unidad sólo necesita la
   * de vuelta.
   */
  const rutaDeLaLista = brazo === null ? null : rutaDeLaPantalla(brazo, SEGMENTOS.botonesDePlantillas)

  /** `CA-27` · «Cancelar» y Escape vuelven a la lista **sin ningún pedido**. */
  const cerrarElPanel = useCallback((): void => {
    setGuardando(QUIETO)
    if (editando !== null) {
      /* ⛔ «Editar» no tiene dirección propia: cerrarlo NO navega (`CA-29`). */
      setEditando(null)
      return
    }
    if (rutaDeLaLista !== null) irA(rutaDeLaLista)
  }, [editando, irA, rutaDeLaLista])

  async function confirmarGuardar(datos: DatosDelPanel): Promise<void> {
    if (brazo === null || sello === null) return
    setGuardando({ enCurso: true, error: null })
    const enEdicion: AutoReplyId | null = editando?.id ?? null
    try {
      if (enEdicion === null) await borde.crear(brazo, datos)
      else await borde.editar(brazo, enEdicion, datos)
      setGuardando(QUIETO)
      /*
       * ⭐ `CA-22`/`CA-33`/`CA-34` · éxito ⇒ **se cierra Y se recarga la lista**.
       * Las dos: sin el `recargar()`, la vuelta mostraría la tabla vieja y el
       * operador concluiría que no se guardó.
       */
      setEditando(null)
      recargar()
      if (enEdicion === null && rutaDeLaLista !== null) irA(rutaDeLaLista)
    } catch (causa: unknown) {
      /*
       * ⛔⛔ `CA-17`/`CA-31` · el `409` se REDACTA acá con el texto que se MANDÓ
       * (`ADR-0201`, ver `./panel.tsx` §⑤). Cualquier otro rechazo se dice tal
       * cual vino: el panel NO se cierra en ningún caso.
       */
      setGuardando({
        enCurso: false,
        error: esRepetida(causa)
          ? mensajeDeRepetida(datos.buttonText, sello)
          : mensajeDe(causa),
      })
    }
  }

  async function confirmarEliminar(): Promise<void> {
    if (brazo === null || eliminando === null) return
    setBorrando({ enCurso: true, error: null })
    try {
      await borde.eliminar(brazo, eliminando.id)
      setEliminando(null)
      setBorrando(QUIETO)
      recargar()
    } catch (causa: unknown) {
      setBorrando({ enCurso: false, error: mensajeDe(causa) })
    }
  }

  const filas = estado.caso === "traido" ? estado.respuestas : []
  const total = estado.caso === "traido" ? estado.total : 0
  /** ⛔ El panel se dibuja por la DIRECCIÓN o por el estado de «Editar», nunca los dos. */
  const panelAbierto = editando !== null || panelNuevo

  return (
    <>
      {/* ⭐ `CA-05` · la nota que explica el reparto de trabajo con Meta. */}
      <p className="plantillas__nota">{NOTA_DE_LA_PESTANA}</p>

      {brazo === null && (
        <p className="plantillas__vacio" role="status">
          Esta pantalla muestra las respuestas automáticas <strong>de un brazo</strong>, y ni la
          ruta actual ni la configuración de esta instancia declaran ninguno. Ábrela desde el menú
          del brazo.
        </p>
      )}

      {estado.caso === "pidiendo" && (
        <span className="plantillas__estado-carga" role="status">
          Trayendo las respuestas automáticas…
        </span>
      )}

      {/*
        ⛔ Ver `EstadoDeLaLista`: «no se pudo preguntar» tiene su propio cartel y
        NO cae en el estado vacío. Cero filas sólo significa «no hay» cuando la
        lectura llegó.
      */}
      {estado.caso === "no-se-pudo-preguntar" && (
        <p className="plantillas__error" role="alert">
          No se pudieron leer las respuestas automáticas de este brazo. {estado.mensaje} Eso{" "}
          <strong>no es «no hay ninguna»</strong>: es que esta vez no se pudo preguntar.
        </p>
      )}

      {/* ⭐ `PRD_R2.md:1096` · el estado vacío, que es un RESULTADO y no un fallo. */}
      {estado.caso === "traido" && filas.length === 0 && (
        <p className="plantillas__vacio">
          Todavía no hay respuestas automáticas en {sello ?? "este brazo"}. Usa «Nueva respuesta
          automática».
        </p>
      )}

      {filas.length > 0 && (
        <>
          <div className="tabla-rodante">
            <table className="plantillas__tabla">
              {/*
                ⭐ `CA-06` · la leyenda EMPIEZA nombrando la zona, y acá pesa como
                en la pestaña de al lado: los dos brazos son dos WABA con el mismo
                juego de plantillas, así que sus botones se ven casi iguales.
              */}
              <caption className="plantillas__caption">
                {sello === null
                  ? "Botones de respuestas automáticas."
                  : `Botones de respuestas automáticas de ${sello}.`}
              </caption>
              <thead>
                <tr>
                  {COLUMNAS.map((columna) => (
                    <th key={columna} scope="col">
                      {columna}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filas.map((fila) => (
                  <tr key={fila.id}>
                    <td>{fila.buttonText}</td>
                    {/*
                      ⭐ `CA-08`/`CA-09` · ver el bloque ②: acá sólo se REDACTA lo
                      que el servidor calculó. El separador es «·» con espacios,
                      como `PRD_R2.md:1092`.
                    */}
                    <td>
                      {fila.apareceEn.length === 0 ? SIN_PLANTILLAS : fila.apareceEn.join(" · ")}
                    </td>
                    <td>{recortar(fila.replyText)}</td>
                    <td>{ROTULO_EN_LA_TABLA[fila.action]}</td>
                    {/*
                      ⭐ `CA-11` · las DOS acciones de fila, y ninguna más.
                      ⚠️ «Eliminar» va en la variante CALLADA de `--peligro`: abrir
                      una confirmación no es destructivo. El rojo sólido es del
                      «Sí, eliminar respuesta» de adentro del popup.
                    */}
                    <td>
                      <button
                        type="button"
                        className="boton boton--chico"
                        onClick={() => {
                          setGuardando(QUIETO)
                          setEditando(fila)
                        }}
                      >
                        Editar
                      </button>
                      <button
                        type="button"
                        className="boton boton--chico boton--peligro"
                        onClick={() => {
                          setBorrando(QUIETO)
                          setEliminando(fila)
                        }}
                      >
                        Eliminar
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/*
            ⭐ `CA-72` / regla 19 · «Anterior · Página N de M · Siguiente», con la
            primitiva compartida y no una barra propia.

            ⚠️ `total` es el del SERVIDOR (`PaginaDeRespuestas.total`) y **nunca
            `filas.length`**: ver el bloque ③ y el encabezado de `src/paginado.tsx`,
            que declara que pasarle el largo de la página es mentir de forma
            visible en la propia llamada.
          */}
          <Paginado
            etiqueta="respuestas automáticas"
            pagina={pagina}
            total={total}
            tamanoDePagina={FILAS_POR_PAGINA}
            onPagina={setPagina}
            unidad={{ singular: "respuesta automática", plural: "respuestas automáticas" }}
          />
        </>
      )}

      {/* ⭐ Ver el bloque ④: una dirección o un estado, el MISMO componente. */}
      {panelAbierto && sello !== null && (
        <PanelDeRespuesta
          sello={sello}
          fila={editando}
          derivacionHabilitada={derivacionHabilitada}
          enCurso={guardando.enCurso}
          error={guardando.error}
          onCancelar={cerrarElPanel}
          onGuardar={(datos) => void confirmarGuardar(datos)}
        />
      )}

      {eliminando !== null && sello !== null && (
        <PopupEliminar
          fila={eliminando}
          sello={sello}
          enCurso={borrando.enCurso}
          error={borrando.error}
          onCancelar={() => {
            /* ⛔ `CA-38` · cancelar cierra el popup y **no llama al borde**. */
            setEliminando(null)
            setBorrando(QUIETO)
          }}
          onConfirmar={() => void confirmarEliminar()}
        />
      )}
    </>
  )
}
